import { useRef, useState } from "react";
import type {
  XeroConnection,
  XeroEmployeePreview,
  XeroEmployeeImportResult,
} from "../../backend/api/xero";
import {
  useImportXeroEmployees,
  useXeroEmployees,
} from "../../backend/queries/xero";
import {
  DataView,
  EmptyState,
  FilterBar,
  StatusBadge,
} from "../../components/patterns";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const employeeName = (employee: XeroEmployeePreview) =>
  [employee.firstname, employee.lastname].filter(Boolean).join(" ") ||
  employee.email ||
  "Unnamed employee";

export default function XeroEmployeeImportDialog({
  connection,
  onClose,
  onImported,
}: {
  connection: Pick<XeroConnection, "tenant_id" | "tenant_name">;
  onClose: () => void;
  onImported: (result: XeroEmployeeImportResult) => void;
}) {
  const employees = useXeroEmployees(connection.tenant_id);
  const importing = useImportXeroEmployees();
  const [selection, setSelection] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const returnFocus = useRef(document.activeElement);
  const rows = employees.data ?? [];
  const selected = rows.filter(
    (employee) =>
      employee.import_error === null && selection.has(employee.employee_id),
  );
  const term = search.trim().toLocaleLowerCase();
  const filtered = rows.filter((employee) =>
    `${employeeName(employee)} ${employee.email ?? ""} ${employee.status ?? ""}`
      .toLocaleLowerCase()
      .includes(term),
  );
  const selectable = filtered.filter(
    (employee) => employee.import_error === null,
  );

  function select(employeeId: string, checked: boolean) {
    setSelection((current) => {
      const next = new Set(current);
      if (checked) next.add(employeeId);
      else next.delete(employeeId);
      return next;
    });
  }

  async function importSelected() {
    setProblem(null);
    try {
      const result = await importing.mutateAsync({
        tenantId: connection.tenant_id,
        payload: {
          employee_ids: selected.map((employee) => employee.employee_id),
        },
      });
      onImported(result);
      onClose();
    } catch (error) {
      setProblem(
        error instanceof Error
          ? error.message
          : "Could not import the selected employees. Your selection is ready to retry.",
      );
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !importing.isPending) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[90dvh] max-w-4xl flex-col"
        onEscapeKeyDown={(event) => {
          if (importing.isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (importing.isPending) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (returnFocus.current instanceof HTMLElement) {
            event.preventDefault();
            returnFocus.current.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Choose employees to import</DialogTitle>
          <DialogDescription>
            Select people from {connection.tenant_name}. New portal accounts
            need a separate invitation before they can sign in. New inactive
            accounts are disabled.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 space-y-4 overflow-y-auto">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchLabel="Search Xero employees"
            searchPlaceholder="Search name, email or status"
            summary={`${selected.length} selected · ${filtered.length} shown`}
            actions={
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={importing.isPending || selectable.length === 0}
                  onClick={() =>
                    setSelection(
                      (current) =>
                        new Set([
                          ...Array.from(current),
                          ...selectable.map((employee) => employee.employee_id),
                        ]),
                    )
                  }
                >
                  Select shown
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={importing.isPending || selection.size === 0}
                  onClick={() => setSelection(new Set())}
                >
                  Clear selection
                </Button>
              </>
            }
          />
          {problem && (
            <Alert variant="destructive">
              <AlertTitle>Import could not finish</AlertTitle>
              <AlertDescription>{problem}</AlertDescription>
              <Button
                variant="outline"
                size="sm"
                disabled={employees.isFetching || importing.isPending}
                onClick={() => {
                  setProblem(null);
                  void employees.refetch();
                }}
              >
                Reload employee list
              </Button>
            </Alert>
          )}
          <DataView<XeroEmployeePreview>
            caption="Xero employees"
            rows={filtered}
            getRowKey={(employee) => employee.employee_id}
            status={
              employees.isPending
                ? "loading"
                : employees.isError
                  ? "error"
                  : "ready"
            }
            errorTitle="Could not load Xero employees"
            errorDescription={employees.error?.message}
            onRetry={() => {
              void employees.refetch();
            }}
            empty={
              <EmptyState
                title={term ? "No matching employees" : "No employees found"}
                description={
                  term
                    ? "Try a different name or email address."
                    : "Add employees in Xero Payroll, then reopen this list."
                }
              />
            }
            columns={[
              {
                id: "select",
                header: "Select",
                cell: (employee) => (
                  <Checkbox
                    className="min-h-target min-w-target"
                    aria-label={`Select ${employeeName(employee)}`}
                    checked={
                      selection.has(employee.employee_id) &&
                      employee.import_error === null
                    }
                    disabled={
                      importing.isPending || employee.import_error !== null
                    }
                    onCheckedChange={(checked) =>
                      select(employee.employee_id, checked === true)
                    }
                  />
                ),
              },
              {
                id: "name",
                header: "Employee",
                primary: true,
                cell: (employee) => (
                  <div>
                    <span className="font-medium">
                      {employeeName(employee)}
                    </span>
                    {employee.import_error && (
                      <p className="text-sm text-tone-warning-foreground">
                        {employee.import_error}
                      </p>
                    )}
                  </div>
                ),
              },
              {
                id: "email",
                header: "Email",
                cell: (employee) => employee.email || "No email address",
              },
              {
                id: "status",
                header: "Xero status",
                cell: (employee) => (
                  <StatusBadge
                    tone={employee.status === "ACTIVE" ? "success" : "neutral"}
                  >
                    {employee.status || "Unknown"}
                  </StatusBadge>
                ),
              },
            ]}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={importing.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            disabled={
              importing.isPending ||
              employees.isPending ||
              employees.isError ||
              selected.length === 0
            }
            onClick={importSelected}
          >
            {importing.isPending
              ? "Importing employees..."
              : selected.length
                ? `Import ${selected.length} employee${selected.length === 1 ? "" : "s"}`
                : "Import selected employees"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
